import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { Mail, Lock, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import LanguageSelector from '../../components/LanguageSelector';
import { Turnstile } from '../../components/Turnstile';
import GoogleSignInButton from '../../components/GoogleSignInButton';

function useErrorMessage(code: string): string {
  const { t } = useTranslation();
  if (!code) return '';
  const key = `portal.login.errors.${code}`;
  const translated = t(key);
  // If key not found, i18next returns the key itself
  return translated !== key ? translated : t('portal.login.errors.unknown');
}

export default function ClientLoginPage() {
  const { login, requestMagicLink } = useClientAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [mode, setMode] = useState<'magic' | 'password'>('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [magicSent, setMagicSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [tsReset, setTsReset] = useState(0);
  // A Turnstile token is single-use — refresh it after any failed attempt so a
  // retry with the correct password isn't rejected with captcha_failed.
  const refreshCaptcha = () => { setTurnstileToken(''); setTsReset(k => k + 1); };

  // Surface OAuth failures redirected back as ?error=google_… by the callback.
  useEffect(() => {
    const e = searchParams.get('error');
    if (e) setErrorCode(e);
  }, [searchParams]);

  const error = useErrorMessage(errorCode);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorCode('');
    setLoading(true);
    try {
      await requestMagicLink(email, undefined, turnstileToken);
      setMagicSent(true);
    } catch (err) {
      setErrorCode(err instanceof Error ? err.message : 'unknown');
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorCode('');
    setLoading(true);
    try {
      await login(email, password, turnstileToken);
      navigate('/portal/dashboard');
    } catch (err) {
      setErrorCode(err instanceof Error ? err.message : 'unknown');
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  if (magicSent) {
    return (
      <div className="min-h-screen flex items-center justify-center relative p-4">
        <img src="/login-bg.avif" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/60" />
        {/* Language selector — top right */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSelector />
        </div>
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-teal-100 flex items-center justify-center mb-6">
            <CheckCircle className="text-teal-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('portal.login.checkEmail')}</h2>
          <p className="text-slate-600 mb-6">
            <Trans i18nKey="portal.login.linkSent" values={{ email }} components={{ strong: <strong className="text-slate-900" /> }} />
            <br />{t('portal.login.linkExpiry')}
          </p>
          <button
            onClick={() => { setMagicSent(false); setEmail(''); }}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            {t('portal.login.useAnother')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative p-4" style={{ background: '#0f172a' }}>
      <picture className="absolute inset-0 w-full h-full">
        <source srcSet="/login-bg.avif" type="image/avif" />
        <img src="/login-bg.png" alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
      </picture>
      <div className="absolute inset-0 bg-slate-900/60" />
      {/* Language selector — top right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="https://kanarix.es" target="_blank" rel="noopener noreferrer" aria-label="Kanarix.es">
            <img src="/kanarix-logo-white.png" alt="Kanarix" className="h-auto max-h-20 w-auto mx-auto mb-4" />
          </a>
          <h1 className="text-2xl font-bold text-white">{t('portal.login.title')}</h1>
          <p className="text-slate-400 mt-1">{t('portal.login.subtitle')}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Mode toggle */}
          <div className="flex rounded-lg bg-slate-100 p-1 mb-6">
            <button
              onClick={() => setMode('magic')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'magic' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              {t('portal.login.magicLink')}
            </button>
            <button
              onClick={() => setMode('password')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'password' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              {t('portal.login.password')}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-rose-50 text-rose-700 text-sm border border-rose-200">
              {error}
            </div>
          )}

          {mode === 'magic' ? (
            <form onSubmit={handleMagicLink} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.login.emailLabel')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t('portal.login.emailPlaceholder')}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  />
                </div>
              </div>
              <Turnstile onVerify={setTurnstileToken} resetKey={tsReset} />
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-medium rounded-lg transition-colors"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                {t('portal.login.sendLink')}
              </button>
              <p className="text-xs text-slate-500 text-center">
                {t('portal.login.sendLinkHint')}
              </p>
            </form>
          ) : (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.login.emailLabel')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder={t('portal.login.emailPlaceholder')}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('portal.login.passwordLabel')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={t('portal.login.passwordPlaceholder')}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                  />
                </div>
              </div>
              <Turnstile onVerify={setTurnstileToken} resetKey={tsReset} />
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-medium rounded-lg transition-colors"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                {t('portal.login.signIn')}
              </button>
              <button
                type="button"
                onClick={() => setMode('magic')}
                className="w-full text-sm text-teal-600 hover:text-teal-700 font-medium"
              >
                {t('portal.login.noPassword')}
              </button>
              <button
                type="button"
                onClick={() => setMode('magic')}
                className="w-full text-xs text-slate-500 hover:text-slate-700"
              >
                {t('portal.login.forgot', '¿Olvidaste tu contraseña? Entra con un enlace mágico y cámbiala en Ajustes.')}
              </button>
            </form>
          )}

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">{t('portal.login.or', 'o')}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <GoogleSignInButton />

          <div className="mt-6 pt-5 border-t border-slate-100 text-center text-sm text-slate-500">
            {t('portal.login.noAccount')}{' '}
            <Link to="/portal/register" className="text-teal-600 hover:text-teal-700 font-medium">
              {t('portal.login.register')}
            </Link>
          </div>
        </div>

        <p className="text-center text-slate-500 text-xs mt-6">
          {t('portal.login.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
}
